import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Architectural image carousel — a single sliding frame with thin chrome:
 * hairline arrow buttons, a JetBrains-Mono counter (01 / 05) and tick-mark
 * indicators. Keyboard arrows when focused, swipe on touch. Monochrome, built
 * to sit on the anthracite Taurus palette.
 */
export default function Carousel({
  images = [],
  alt = "",
  aspect = "aspect-[16/10]",
  className = "",
}) {
  const [i, setI] = useState(0);
  const n = images.length;
  const startX = useRef(null);

  const go = useCallback((d) => setI((p) => (p + d + n) % n), [n]);
  const pad = (k) => String(k + 1).padStart(2, "0");

  useEffect(() => {
    setI(0);
  }, [n]);

  if (!n) return null;

  const onKey = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    }
  };

  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 44) go(dx < 0 ? 1 : -1);
    startX.current = null;
  };

  return (
    <div className={className}>
      <div
        className="group relative select-none overflow-hidden border border-white/12 bg-black/30 outline-none"
        tabIndex={0}
        onKeyDown={onKey}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        role="region"
        aria-roledescription="carousel"
        aria-label={alt}
      >
        {/* Sliding track */}
        <div
          className={`flex ${aspect} w-full transition-transform [transition-duration:900ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]`}
          style={{ transform: `translateX(-${i * 100}%)` }}
        >
          {images.map((src, k) => (
            <img
              key={src}
              src={src}
              alt={`${alt} ${pad(k)}`}
              loading={k === 0 ? "eager" : "lazy"}
              draggable={false}
              className="h-full w-full flex-shrink-0 basis-full object-cover"
            />
          ))}
        </div>

        {/* Top hairline + counter */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-4 sm:p-5">
          <span className="font-rate text-[11px] tracking-[0.25em] text-white/85">
            {pad(i)} <span className="text-white/35">/ {pad(n - 1)}</span>
          </span>
        </div>

        {/* Arrows */}
        <button
          type="button"
          aria-label="Previous image"
          onClick={() => go(-1)}
          className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/30 text-white opacity-0 backdrop-blur-sm transition-all duration-300 hover:border-white hover:bg-white hover:text-[#16181a] focus-visible:opacity-100 group-hover:opacity-100 sm:left-5"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          aria-label="Next image"
          onClick={() => go(1)}
          className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/30 text-white opacity-0 backdrop-blur-sm transition-all duration-300 hover:border-white hover:bg-white hover:text-[#16181a] focus-visible:opacity-100 group-hover:opacity-100 sm:right-5"
        >
          <ChevronRight size={20} />
        </button>

        {/* Bottom gradient for legibility of the ticks */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent" />
      </div>

      {/* Tick-mark indicators */}
      <div className="mt-5 flex items-center justify-center gap-2">
        {images.map((_, k) => (
          <button
            key={k}
            type="button"
            aria-label={`Go to image ${pad(k)}`}
            onClick={() => setI(k)}
            className={`h-px transition-all duration-500 ${
              k === i ? "w-10 bg-white" : "w-5 bg-white/25 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
